import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@aqx-core/common/api/auth';
import { EWebtraderResetPasswordType } from '@aqx-core/common/api/config';
import { useAuthLoading } from '@aqx-core/common/services/auth';

import { useAuthRoutes } from './use-auth-routes';

interface IUseLoginForgotPasswordAction {
  selectedTab: ELoginAs | undefined;
  /* Pass in the navigate function which navigates to forgot password CRM screen/route */
  navigateForgotPasswordCRM: () => void;
  /* Pass in the navigate function which navigates to forgot password live screen/route */
  navigateForgotPasswordLive: () => void;
  /* Pass in the navigate function which navigates to reset password screen/route */
  navigateResetPassword: () => void;
  /* Pass in the open url function which accepts a url string  */
  openUrlCallback: (url: string) => void;
}

export const useLoginForgotPasswordAction = ({
  selectedTab,
  navigateForgotPasswordCRM,
  navigateForgotPasswordLive,
  navigateResetPassword,
  openUrlCallback
}: IUseLoginForgotPasswordAction) => {
  const { t } = useTranslation();
  const authLoginLoading = useAuthLoading();
  const { hasCrmForgotPasswordButton, hasLiveForgotPasswordButton, hasDemoResetPassword, companyProfile } =
    useAuthRoutes();

  const {
    webtraderResetPasswordType,
    crmForgotPasswordUrl = '',
    webtraderResetPasswordUrl = '',
    crmForgotPasswordType
  } = companyProfile || {};

  const isNonCrmIntegrationResetPasswordType = webtraderResetPasswordType === EWebtraderResetPasswordType.INTEGRATION;

  const isCrmIntegrationResetPasswordType = crmForgotPasswordType === EWebtraderResetPasswordType.INTEGRATION;

  const { text: forgotText, onClick } = useMemo(() => {
    const FORGOT_PASSWORD = t('Forgot password');
    const RESET_PASSWORD = t('Reset Password');

    // CRM
    if (selectedTab === ELoginAs.LIVE && hasCrmForgotPasswordButton) {
      const onClick = () =>
        isCrmIntegrationResetPasswordType ? navigateForgotPasswordCRM() : openUrlCallback(crmForgotPasswordUrl);

      return { text: FORGOT_PASSWORD, onClick };
    }
    if (selectedTab && LIVE_LOGINS_WITHOUT_CRM.includes(selectedTab) && hasLiveForgotPasswordButton) {
      const onClick = () =>
        isNonCrmIntegrationResetPasswordType
          ? navigateForgotPasswordLive()
          : openUrlCallback(webtraderResetPasswordUrl);

      return { text: FORGOT_PASSWORD, onClick };
    }
    if (selectedTab && DEMO_LOGINS.includes(selectedTab) && hasDemoResetPassword) {
      const onClick = () => navigateResetPassword();
      return { text: RESET_PASSWORD, onClick };
    }
    return { text: '', onClick: () => {} };
  }, [
    crmForgotPasswordUrl,
    hasCrmForgotPasswordButton,
    hasDemoResetPassword,
    hasLiveForgotPasswordButton,
    isCrmIntegrationResetPasswordType,
    isNonCrmIntegrationResetPasswordType,
    navigateForgotPasswordCRM,
    navigateForgotPasswordLive,
    navigateResetPassword,
    openUrlCallback,
    selectedTab,
    t,
    webtraderResetPasswordUrl
  ]);

  const onClickHandler = useCallback(() => {
    if (authLoginLoading) {
      return;
    }
    onClick();
  }, [authLoginLoading, onClick]);

  return {
    onPress: onClickHandler,
    forgotText
  };
};
